
import { Component, Prop, VModel, Vue, Watch } from "vue-property-decorator";

import { Claim, ClaimCategory, ClaimSource } from "@/types";
import * as ClaimCategoryService from "@/services/ClaimCategoryService";
import * as FactcheckService from "@/services/FactCheckService";

@Component
export default class ClaimForm extends Vue {
  @VModel() claim!: Claim;

  @Prop() prefillClaimCategories!: ClaimCategory[];
  @Prop() prefilledMetadata!: Record<string, {
    confidence: string;
    sourceQuote: string;
    originalUrl: string;
  }>;

  addNewClaimSource() {
    this.claim.ClaimSources.push({
      source: '',
      platform: null,
      mediaformat: null,
      associatedMediaType: null,
      associatedMedia: null,
      date: null,
      politicalParty: null,
    });
  }

  removeClaimSource(index: number) {
    if (this.claim.ClaimSources.length > 1) {
      this.claim.ClaimSources.splice(index, 1);
    }
  }

  sourceDateMenus = [];
  noDateForSourceIndexes = [];

  removeClaimant(claimant: string) {
    const idx = this.claim.claimants.indexOf(claimant);
    if (idx !== -1) {
      this.claim.claimants.splice(idx, 1);
    }
  }

  search = "";

  selectedClaimCategories: ClaimCategory[] = [];

  @Watch("prefillClaimCategories", { deep: true })
  onClaimChange() {
    if (this.prefillClaimCategories) {
      this.selectedClaimCategories = this.prefillClaimCategories;
    }
  }

  @Watch("claim", { deep: true })
  onClaimPropChange() {
    // Update noDate when claim prop changes
    for (let i = 0; i < this.claim.ClaimSources.length; i++) {
      this.$set(this.noDateForSourceIndexes, i, this.claim.ClaimSources[i].date === null);
    }
  }

  onClaimantChange(newValue: string) {
    console.log("New claimant added:", newValue);
    if (newValue && !this.claim.claimants.includes(newValue)) {
      this.claim.claimants.push(newValue);
    }
  }

  setDateToNull(index: number) {
    if (this.noDateForSourceIndexes[index]) {
      this.claim.ClaimSources[index].date = null;
    }
  }

  formatDate(d: string): string {
    if (!d) {
      return '';
    }
    const date = new Date(d);
    return date.toLocaleDateString("nl-BE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  claimCategories: ClaimCategory[] = [];
  claimants: string[] = [];

  politicalParties = [
    {
      name: "Open Vld/MR: Alliance of Liberals and Democrats for Europe Party (ALDE)",
      value: "Alliance of Liberals and Democrats for Europe Party (ALDE)",
    },
    {
      name: "European Christian Political Movement (ECPM)",
      value: "European Christian Political Movement (ECPM)",
    },
    {
      name: "N-VA:European Conservatives and Reformists Party (ECR Party)",
      value: "European Conservatives and Reformists Party (ECR Party)",
    },
    {
      name: "European Democratic Party (EDP)",
      value: "European Democratic Party (EDP)",
    },
    {
      name: "Groen/Ecolo/PVDA/PTB: European Free Alliance (EFA)",
      value: "European Free Alliance (EFA)",
    },
    {
      name: "Groen/Ecolo: European Green Party (EFP)",
      value: "European Green Party (EFP)",
    },
    {
      name: "CD&V: European People’s Party (EPP)",
      value: "European People’s Party (EPP)",
    },
    {
      name: "Vlaams Belang: Identity and Democracy Party (ID Party)",
      value: "Identity and Democracy Party (ID Party)",
    },
    {
      name: "Niet-fractiegebonden leden",
      value: "Non-Inscrits (NI)",
    },
    {
      name: "PVDA/PTB: Party of the European Left (PEL)",
      value: "Party of the European Left (PEL)",
    },
    {
      name: "Vooruit/PS: Party of European Socialists (PES)",
      value: "Party of European Socialists (PES)",
    },
    {
      name: "Andere partij",
      value: "Other party",
    },
  ];

  mediaFormats = [
    {
      code: "image",
      name: "Afbeelding",
    },
    {
      code: "video",
      name: "Video",
    },
    {
      code: "audio",
      name: "Audio",
    },
    {
      code: "text",
      name: "Tekst",
    },
    {
      code: "other",
      name: "Andere",
    },
  ];

  socialMediaPlatforms = [
    { name: "X", code: "x" },
    { name: "Facebook", code: "facebook" },
    { name: "Instagram", code: "instagram" },
    { name: "TikTok", code: "tiktok" },
    { name: "YouTube", code: "youtube" },
    { name: "WhatsApp", code: "whatsapp" },
    { name: "Telegram", code: "telegram" },
    { name: "Signal", code: "signal" },
    { name: "LinkedIn", code: "linkedin" },
    { name: "Pinterest", code: "pinterest" },
    { name: "Snapchat", code: "snapchat" },
    { name: 'Threads', code: 'threads'},
    { name: "Other", code: "other" },
  ];

  countries = [
    {
      name: "België",
      code: "BE",
    },
    {
      name: "Nederland",
      code: "NL",
    },
    {
      name: "Duitsland",
      code: "DE",
    },
  ];

  async mounted() {
    const categories = await ClaimCategoryService.GetClaimCategories();
    // sort by categories.nl
    categories.sort((a, b) => a.nl.localeCompare(b.nl));
    this.claimCategories = categories;
    this.claimants = await FactcheckService.GetClaimants();

    if (this.claim.ClaimCategories) {
      this.selectedClaimCategories = this.claim.ClaimCategories;
    }

    // Initialize with one source if array is empty
    if (this.claim.ClaimSources.length === 0) {
      this.addNewClaimSource();
    }
  }

  @Watch("selectedClaimCategories", { deep: true })
  onSelectedClaimCategoriesChange() {
    this.$emit("categories", this.selectedClaimCategories);
  }

  // Helper method to truncate quotes for display
  truncateQuote(quote: string, maxLength = 100): string {
    if (!quote || quote.length <= maxLength) {
      return quote;
    }
    return quote.substring(0, maxLength) + '...';
  }

  // Helper method to get color based on confidence level
  getConfidenceColor(confidence: string): string {
    switch (confidence.toLowerCase()) {
      case 'high':
        return 'success';
      case 'medium':
        return 'warning';
      case 'low':
        return 'error';
      default:
        return 'info';
    }
  }

  // Helper method to emit source quote data to parent
  openSourceQuote(fieldName: string) {
    if (this.prefilledMetadata && this.prefilledMetadata[fieldName]) {
      const metadata = this.prefilledMetadata[fieldName];
      this.$emit('open-source-quote', {
        url: metadata.originalUrl,
        text: metadata.sourceQuote
      });
    }
  }
}
